import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { IconButton } from '@cbrebuild/blocks';
import {
  LeaseAbstractionField,
  LeaseAbstractionFieldType,
} from '../../../leaseAbstractionField';

import '../../../lease-abstraction.scss';

const parseBooleanValue = value => value === true || String(value).toLowerCase() === 'true';

const normalizeCustomEscalationPayload = schedule => schedule.map(item => ({
  months: parseInt(item.months, 10) || 0,
  rent_dollar: Number(item.rent?.rent_dollar) || 0,
  rent_percent: Number(item.rent?.rent_percent) || 0,
  is_percent: parseBooleanValue(item.rent?.is_percent),
}));

const CustomLeaseEscalationField = ({
  modalFieldState,
  modalFieldChangeHandler,
  individualModalFieldChangeHandler,
  fieldErrorMap,
  showPageValidation,
  isRequired,
  currentCustomLeaseEscalation,
  escalationIndex,
}) => {
  const convertToYearsAndMonths = (totalMonths) => {
    const years = Math.floor((totalMonths || 0) / 12);
    const months = (totalMonths || 0) % 12;
    return { years, months };
  };

  const derived = convertToYearsAndMonths(currentCustomLeaseEscalation.months);
  // Local display state lets the user clear the field while typing.
  // On blur (individualFieldChangeHandler) the value is committed to shared state.
  const [localYears, setLocalYears] = useState(String(derived.years));
  const [localMonths, setLocalMonths] = useState(String(derived.months));

  // Re-sync local display state when the underlying months value arrives from the DB
  // (the DB-init useEffect in LeaseCompAbstractionTab fires after mount, so the
  // useState initializer above would have captured 0/undefined on first render).
  useEffect(() => {
    const updated = convertToYearsAndMonths(currentCustomLeaseEscalation.months);
    setLocalYears(String(updated.years));
    setLocalMonths(String(updated.months));
  }, [currentCustomLeaseEscalation.id, currentCustomLeaseEscalation.months]);

  const handleCustomFieldChange = (event, field) => {
    const currentValue = [...modalFieldState.customLeaseEscalation.value];
    const newItem = { ...currentCustomLeaseEscalation };
    const { months, years } = convertToYearsAndMonths(currentCustomLeaseEscalation.months);
    switch (field) {
      case 'rent':
        newItem.rent = event.value;
        break;
      case 'years':
        newItem.months = (parseFloat(event.target.value || 0) * 12) + months;
        break;
      case 'months':
        newItem.months = (years * 12) + parseFloat(event.target.value || 0);
        break;
      default:
        break;
    }
    currentValue.splice(
      currentValue.findIndex(r => r.id === newItem.id),
      1,
      newItem,
    );
    modalFieldChangeHandler('customLeaseEscalation', { value: currentValue });
    if (field === 'rent') {
      const child_value = normalizeCustomEscalationPayload(currentValue);
      individualModalFieldChangeHandler({
        child_value,
        parent_key: 'comp_info',
        child_key: modalFieldState.customLeaseEscalation.trackingID,
        stateVariable: 'customLeaseEscalation',
      });
    }
    // Return the freshly computed array so callers can pass it directly to
    // handleIndividualFieldChange, avoiding the stale-state read problem.
    return currentValue;
  };

  // Accepts an optional pre-computed updatedValue to avoid reading stale React state.
  // When not provided (e.g. called standalone), falls back to current state.
  const handleIndividualFieldChange = (updatedValue) => {
    const source = updatedValue || modalFieldState.customLeaseEscalation.value;
    const child_value = normalizeCustomEscalationPayload(source);
    individualModalFieldChangeHandler({
      child_value,
      parent_key: 'comp_info',
      child_key: modalFieldState.customLeaseEscalation.trackingID,
      stateVariable: 'customLeaseEscalation',
    });
  };

  // 0 is a valid entry — only treat null/undefined/'' as empty
  const rentIsEmpty = v => v === null || v === undefined || v === '';
  const isMonthsEmpty = currentCustomLeaseEscalation.months == null || currentCustomLeaseEscalation.months === '';
  const rentLabel = `Rent ${escalationIndex + 1}`;

  return (
    <div className="file-modal-flex-group">
      <LeaseAbstractionField
        asGroup
        percentDollarHideConversion
        customPercentDollarField={{
          percentage: 'rent_percent',
          value: 'rent_dollar',
          select: 'is_percent',
        }}
        key="escalationPerYear"
        fieldType={LeaseAbstractionFieldType.PERCENT_DOLLAR}
        isRequired={isRequired}
        fieldData={{
          data: { value: currentCustomLeaseEscalation.rent },
          label: rentLabel,
          errorMessage: (
            showPageValidation &&
            rentIsEmpty(currentCustomLeaseEscalation.rent?.rent_dollar) &&
            rentIsEmpty(currentCustomLeaseEscalation.rent?.rent_percent)
          ) ? 'Required' : fieldErrorMap.escalationPerYear,
          key: 'customLeaseEscalation',
        }}
        fieldChangeHandler={event => handleCustomFieldChange(event, 'rent')}
        individualFieldChangeHandler={() => handleIndividualFieldChange()}
      />
      <LeaseAbstractionField
        asGroup
        key="freeRentYears"
        fieldType={LeaseAbstractionFieldType.TEXT_WITH_LABEL}
        fieldData={{
          data: { value: localYears, toolTip: {} },
          label: 'Year',
          errorMessage: (showPageValidation && isMonthsEmpty) ? 'Year is required' : fieldErrorMap.freeRentYears,
          key: 'customLeaseEscalation',
        }}
        fieldChangeHandler={(event) => {
          const v = event.target.value;
          if (/^\d*(\.\d{0,2})?$/.test(v)) setLocalYears(v);
        }}
        individualFieldChangeHandler={() => {}}
        onBlur={() => {
          // leaseAbstractionField calls onBlur() with no arguments, so read
          // the current local state directly instead of event.target.value.
          const committed = localYears === '' ? '0' : localYears;
          setLocalYears(committed);
          const updated = handleCustomFieldChange({ target: { value: committed } }, 'years');
          handleIndividualFieldChange(updated);
        }}
      />
      <LeaseAbstractionField
        asGroup
        key="freeRentMonths"
        fieldType={LeaseAbstractionFieldType.TEXT_WITH_LABEL}
        fieldData={{
          data: { value: localMonths, toolTip: {} },
          label: 'Month',
          errorMessage: (showPageValidation && isMonthsEmpty) ? 'Month is required' : fieldErrorMap.freeRentMonths,
          key: 'customLeaseEscalation',
        }}
        fieldChangeHandler={(event) => {
          const v = event.target.value;
          if (/^\d*(\.\d{0,2})?$/.test(v)) setLocalMonths(v);
        }}
        individualFieldChangeHandler={() => {}}
        onBlur={() => {
          const committed = localMonths === '' ? '0' : localMonths;
          setLocalMonths(committed);
          const updated = handleCustomFieldChange({ target: { value: committed } }, 'months');
          handleIndividualFieldChange(updated);
        }}
      />
      <IconButton
        className="blxs-button-icon-small"
        iconName="close-circle"
        onClick={() => {
          const newValue = modalFieldState.customLeaseEscalation.value.filter(item => item.id !== currentCustomLeaseEscalation.id);
          modalFieldChangeHandler('customLeaseEscalation', { value: newValue });
          const child_value = normalizeCustomEscalationPayload(newValue);
          individualModalFieldChangeHandler({
            child_value,
            parent_key: 'comp_info',
            child_key: modalFieldState.customLeaseEscalation.trackingID,
            stateVariable: 'customLeaseEscalation',
          });
        }}
        variant="basic"
      />
    </div>
  );
};

CustomLeaseEscalationField.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  modalFieldState: PropTypes.object,
  modalFieldChangeHandler: PropTypes.func,
  individualModalFieldChangeHandler: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types
  fieldErrorMap: PropTypes.object,
  showPageValidation: PropTypes.bool,
  isRequired: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  currentCustomLeaseEscalation: PropTypes.object,
  escalationIndex: PropTypes.number,
};

CustomLeaseEscalationField.defaultProps = {
  modalFieldState: {},
  modalFieldChangeHandler: () => {},
  individualModalFieldChangeHandler: () => {},
  fieldErrorMap: {},
  showPageValidation: false,
  isRequired: true,
  currentCustomLeaseEscalation: {},
  escalationIndex: 0,
};

export default CustomLeaseEscalationField;
